import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Megaphone, Loader2, Upload, Trash2 } from "lucide-react";
import { MediaView } from "@/components/app/Checkout";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Spinner } from "@/components/app/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, dt } from "@/lib/api";

const F = ({ id, label, ...p }) => <div className="space-y-1.5"><Label htmlFor={id}>{label}</Label><Input id={id} data-testid={`${id}-input`} className="h-11 rounded-xl" {...p} /></div>;

export const Notifications = () => {
  const [p, setP] = useState(null);
  const load = () => api.get("/me/notifications").then(({ data }) => setP(data));
  useEffect(() => { load(); }, []);
  const save = async (next) => {
    try { await api.put("/me/notifications", next); toast.success("Tercihleriniz kaydedildi."); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!p) return <Spinner />;
  const Item = ({ icon: I, k, title, desc, at }) => (
    <div className="glass-card flex items-start justify-between gap-4 p-6">
      <div className="flex gap-4"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-green-500/10 text-green-500"><I className="h-5 w-5" /></span>
        <div><div className="font-semibold">{title}</div><p className="mt-1 text-sm text-muted-foreground">{desc}</p>{p[k] && at && <p className="mt-2 text-xs text-muted-foreground">İzin tarihi: {dt(at)}</p>}</div></div>
      <Switch checked={p[k]} onCheckedChange={(v) => save({ whatsapp_transactional: p.whatsapp_transactional, whatsapp_marketing: p.whatsapp_marketing, [k]: v })} data-testid={`pref-${k}-switch`} />
    </div>
  );
  return (
    <div className="max-w-3xl" data-testid="notifications-page">
      <PageHeader title="Bildirim Ayarları" desc="WhatsApp bildirim izinlerinizi yönetin." />
      {!p.phone && <div className="mb-5 rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="phone-missing-warning">WhatsApp bildirimleri için önce <Link to="/panel/profil" className="font-semibold text-primary">profilinize</Link> telefon numarası ekleyin.</div>}
      <div className="space-y-4">
        <Item icon={MessageCircle} k="whatsapp_transactional" at={p.transactional_consent_at} title="WhatsApp üzerinden işlem bildirimleri almak istiyorum." desc="Satın alma, AI üretimi, kredi iadesi ve düşük bakiye bildirimleri." />
        <Item icon={Megaphone} k="whatsapp_marketing" at={p.marketing_consent_at} title="Kampanya ve pazarlama mesajları" desc="Yeni özellikler, kampanyalar ve özel teklifler. İstediğiniz an kapatabilirsiniz." />
      </div>
    </div>
  );
};

const LogoCard = () => {
  const { user, refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { await api.post("/me/logo", fd); await refresh(); toast.success("Logonuz yüklendi."); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const remove = async () => { try { await api.delete("/me/logo"); await refresh(); toast.success("Logo kaldırıldı."); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="glass-card mt-6 space-y-4 p-6" data-testid="logo-card">
      <div><h2 className="font-semibold">Logo</h2><p className="mt-1 text-sm text-muted-foreground">Video taleplerinizde kullanılmak üzere ekibimize iletilir. Şeffaf arka planlı PNG önerilir.</p></div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="grid h-20 w-36 place-items-center rounded-xl border border-dashed border-border bg-[repeating-conic-gradient(#8881_0_25%,transparent_0_50%)] bg-[length:16px_16px]">
          {user.logo_file_id ? <MediaView id={user.logo_file_id} type="image/png" className="max-h-16 max-w-32 object-contain" /> : <span className="text-xs text-muted-foreground">Logo yok</span>}
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-border px-4 py-2 text-sm font-medium hover:border-primary" data-testid="logo-upload-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{user.logo_file_id ? "Değiştir" : "Logo yükle"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="logo-upload-input" />
        </label>
        {user.logo_file_id && <Button variant="ghost" size="sm" onClick={remove} className="text-rose-500" data-testid="logo-remove-button"><Trash2 className="mr-1 h-4 w-4" />Kaldır</Button>}
      </div>
    </div>
  );
};

export const Profile = () => {
  const { user, refresh } = useAuth();
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name, phone: user.phone || "", address: user.address || "" });
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.put("/me/profile", { ...f, phone: f.phone || null }); await refresh(); toast.success("Profiliniz güncellendi."); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  const changePw = async (e) => {
    e.preventDefault();
    try { await api.put("/me/password", pw); setPw({ current_password: "", new_password: "" }); toast.success("Şifreniz değiştirildi."); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="max-w-3xl" data-testid="profile-page">
      <PageHeader title="Profil" desc={user.email} />
      <form onSubmit={save} className="glass-card space-y-4 p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <F id="profile-first-name" label="Ad" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required />
          <F id="profile-last-name" label="Soyad" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} required />
        </div>
        <F id="profile-phone" label="Telefon" placeholder="05xx xxx xx xx" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
        <F id="profile-address" label="Adres (fatura için)" value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
        <Button type="submit" className="btn-glow rounded-xl" disabled={busy} data-testid="profile-save-button">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Kaydet</Button>
      </form>
      <LogoCard />
      <form onSubmit={changePw} className="glass-card mt-6 space-y-4 p-6">
        <h2 className="font-semibold">Şifre Değiştir</h2>
        <F id="profile-current-password" label="Mevcut şifre" type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} required />
        <F id="profile-new-password" label="Yeni şifre" type="password" minLength={8} value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} required />
        <Button type="submit" variant="outline" className="rounded-xl" data-testid="password-save-button">Şifreyi Güncelle</Button>
      </form>
    </div>
  );
};
